import moment from 'moment';
import { initials, moneyFormatter } from './helper';

// kan-88 built the executive's commission sheet row on the placement detail
// page. kan-98 needs the same row for every direct-hire placement on the
// accounting dashboard, so the row-building rules live here — one definition
// both exports share, and the only place these column rules are written down.

// GraphQL serializes the code-first enum KEYS, so the browser sees the
// uppercase values below — same as splitType 'RECRUITING' / 'ACCOUNT_MANAGER'.
export type CommissionSide = 'RECRUITING' | 'AM';

export interface ICommissionEntry {
  userId: string;
  commissionSide: CommissionSide;
  entryType: 'COMMISSION' | 'CLAWBACK';
  status: 'PROJECTED' | 'EARNED' | 'VOID';
  rateType: 'PERCENTAGE' | 'FLAT_RATE';
  commissionRate: number | null;
  commissionAmount: number;
}

export interface ISheetColumn {
  letter: string;
  name: string;
  // What lands in the clipboard (raw, Excel-friendly).
  value: string;
  // What the modal shows; defaults to `value`.
  display?: string;
}

export interface IBuildCommissionSheetRowArgs {
  /** GET_PLACEMENT_BY_ID shape (agreement?.nickName, salaryAmount, ...). */
  placement: any;
  /** PLACEMENT_SPLITTING shape: { splitType, splitPercentage, user }. */
  placementSplitting: Array<any>;
  /** dhPlacementCommissions rows for this placement. */
  commissions: Array<any>;
}

export interface ICommissionSheetRow {
  columns: Array<ISheetColumn>;
  /** Every split user on both sides has an active commission entry. */
  commissionsComplete: boolean;
}

export const sideOfSplitType = (splitType: string): CommissionSide =>
  splitType === 'RECRUITING' ? 'RECRUITING' : 'AM';

const commissionKey = (userId: string, side: CommissionSide): string =>
  `${userId}|${side}`;

const asNumber = (value: any): string =>
  value === null || value === undefined ? '' : String(value);

// Money columns: rounded to cents so float dust (10499.999999999998) never
// lands in the sheet. The % fee column stays raw.
export const asMoney = (value: any): string =>
  value === null || value === undefined
    ? ''
    : String(Math.round(Number(value) * 100) / 100);

/**
 * The live (non-voided) commission each split user earns on a given side.
 * Duplicates can't exist — the server rejects a second active entry for the
 * same user+side — so the first match is the entry.
 */
export const activeCommissionsByUserSide = (
  commissions: Array<any>
): Map<string, ICommissionEntry> => {
  const byUserSide = new Map<string, ICommissionEntry>();
  for (const entry of (commissions || []) as Array<ICommissionEntry>) {
    if (entry?.entryType !== 'COMMISSION' || entry?.status === 'VOID') continue;
    const key = commissionKey(entry.userId, entry.commissionSide);
    if (!byUserSide.has(key)) byUserSide.set(key, entry);
  }
  return byUserSide;
};

/**
 * Mirrors the dashboard's green dot: every split user on both sides paid.
 * No splits at all means nothing to source the sheet's initials columns from.
 * A split without a user (deactivated) can't have an entry, so it can't be
 * required.
 */
export const hasCompleteCommissions = (
  placementSplitting: Array<any>,
  byUserSide: Map<string, ICommissionEntry>
): boolean => {
  const splitUsers = (placementSplitting || []).filter(
    (split) =>
      !!split?.user?.id &&
      (split?.splitType === 'ACCOUNT_MANAGER' ||
        split?.splitType === 'RECRUITING')
  );

  return (
    !!splitUsers.length &&
    splitUsers.every((split) =>
      byUserSide.has(
        commissionKey(split?.user?.id, sideOfSplitType(split?.splitType))
      )
    )
  );
};

// "-30" for a percentage entry, "-$1500" for a flat rate — no % sign and no
// thousands separators, the sheet's own convention.
const commissionSuffix = (entry?: ICommissionEntry): string => {
  if (!entry) return '';
  if (entry.rateType === 'FLAT_RATE') {
    const amount = asMoney(entry.commissionAmount);
    return amount ? `-$${amount}` : '';
  }
  // The rate is echoed verbatim (no % sign, decimals preserved) — cents
  // rounding is only for money, and a rate isn't money.
  const rate =
    entry.commissionRate === null || entry.commissionRate === undefined
      ? ''
      : String(Number(entry.commissionRate));
  return rate ? `-${rate}` : '';
};

/**
 * The sheet's per-side source cell: bigger split percentage leads. A split
 * whose user is missing (deactivated) shows as "?" rather than silently
 * under-reporting the side, and each user carries their own commission for
 * this side. The exports are gated on full coverage, so a missing entry here
 * means it was voided in another tab — fall back to bare initials instead of
 * blocking the row.
 */
export const sideSourceString = (
  splitType: string,
  placementSplitting: Array<any>,
  byUserSide: Map<string, ICommissionEntry>
): string =>
  [...(placementSplitting || [])]
    .filter((split) => split?.splitType === splitType)
    .sort(
      (a, b) =>
        (Number(b?.splitPercentage) || 0) - (Number(a?.splitPercentage) || 0)
    )
    .map((split) => {
      const label = initials(split?.user?.fullName) || '?';
      const entry = split?.user?.id
        ? byUserSide.get(
            commissionKey(split?.user?.id, sideOfSplitType(splitType))
          )
        : undefined;
      return `${label}${commissionSuffix(entry)}`;
    })
    .join(' | ');

export const buildCommissionSheetRow = ({
  placement,
  placementSplitting,
  commissions,
}: IBuildCommissionSheetRowArgs): ICommissionSheetRow => {
  const commissionByUserSide = activeCommissionsByUserSide(commissions);
  const commissionsComplete = hasCompleteCommissions(
    placementSplitting,
    commissionByUserSide
  );

  const startDate = placement?.startDateString
    ? moment(placement?.startDateString)
    : null;
  const dueDate = startDate?.isValid() ? startDate.clone().add(30, 'days') : null;

  const jobLocation = [placement?.jobCity, placement?.jobStateCode]
    .filter(Boolean)
    .join(', ');

  const isRpo = (placement?.agreement?.nickName || '').startsWith('RPO');
  const client = placement?.companyName
    ? `${placement?.companyName}${isRpo ? '-RPO' : ''}`
    : '';

  const sideInitials = (splitType: string): string =>
    sideSourceString(splitType, placementSplitting, commissionByUserSide);

  const columns: Array<ISheetColumn> = [
    { letter: 'A', name: "Chk Rec'd Date", value: '' },
    { letter: 'B', name: 'Check Number', value: '' },
    { letter: 'C', name: 'Check Amt ($)', value: '' },
    {
      letter: 'D',
      name: 'Invoice Past > 30 Due Day',
      value: dueDate ? dueDate.format('MM/DD/YYYY') : '',
    },
    { letter: 'E', name: 'Aging', value: '' },
    { letter: 'F', name: 'AM Source', value: sideInitials('ACCOUNT_MANAGER') },
    { letter: 'G', name: 'Rec Source', value: sideInitials('RECRUITING') },
    { letter: 'H', name: 'Invoice Number', value: placement?.invoiceNumber || '' },
    { letter: 'I', name: 'Placed Name', value: placement?.candidateName || '' },
    { letter: 'J', name: 'Job Placement Detail', value: placement?.jobTitle || '' },
    { letter: 'K', name: 'Job', value: jobLocation },
    { letter: 'L', name: 'Client', value: client },
    { letter: 'M', name: 'Commission Paid Date', value: '' },
    { letter: 'N', name: 'Commission Owed Date', value: '' },
    {
      letter: 'O',
      name: 'Start Date',
      value: startDate?.isValid() ? startDate.format('MM/DD/YYYY') : '',
    },
    {
      letter: 'P',
      name: 'Acceptance Date',
      value: placement?.createdAt
        ? moment(placement?.createdAt).format('MM/DD/YYYY')
        : '',
    },
    {
      letter: 'Q',
      name: 'Placed Salary',
      value: asMoney(placement?.salaryAmount),
      display: asMoney(placement?.salaryAmount)
        ? moneyFormatter(Number(placement?.salaryAmount) || 0)
        : '',
    },
    {
      letter: 'R',
      name: 'Placement Fee',
      // His sheet's column is %-formatted, so the pasted value is the decimal
      // fraction (25 -> 0.25); rounded to avoid float dust like 0.33330000004.
      value:
        placement?.feeStructured === null || placement?.feeStructured === undefined
          ? ''
          : String(Math.round((Number(placement?.feeStructured) / 100) * 1e6) / 1e6),
      display: asNumber(placement?.feeStructured)
        ? `${placement?.feeStructured}%`
        : '',
    },
    {
      letter: 'S',
      name: 'Billing Credit',
      value: asMoney(placement?.totalCommission),
      display: asMoney(placement?.totalCommission)
        ? moneyFormatter(Number(placement?.totalCommission) || 0)
        : '',
    },
    {
      letter: 'T',
      name: 'Billing Credit Adjustment',
      value: asMoney(placement?.adjustmentTotal),
      display: asMoney(placement?.adjustmentTotal)
        ? moneyFormatter(Number(placement?.adjustmentTotal) || 0)
        : '',
    },
    {
      letter: 'U',
      name: 'Net Billing Credit',
      value: asMoney(placement?.netCommission),
      display: asMoney(placement?.netCommission)
        ? moneyFormatter(Number(placement?.netCommission) || 0)
        : '',
    },
  ];

  return { columns, commissionsComplete };
};
